import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import {
    QuickQuoteService,
    NewItinerariesV3BannerService,
    QUICK_QUOTE_CONSTANTS,
    TravelAgentService
} from '@dcl/dcl-ui-components-library';
import { CruiseListingAnalyticsModel } from '@interfaces/cruise-listing-analytics-model';
import { FilterOptionData } from '@interfaces/filter-option-data';
import { CruiseListingDataService } from '@services/cruise-listing/cruise-listing.service';
import { ConfigService } from '@app/core/config.service';
import { HomepageAnalyticsService } from '@app/services/analytics/analytics.service';
import { ANALYTICS_CONSTANTS } from '@app/services/analytics/analytics.constants';
import { HttpClient } from '@angular/common/http';
import { ENDPOINTS } from '@app/app.constants';
import { ResourceBundleService } from '@app/services/resource-bundle/resource-bundle.service';
import { LocaleService } from '@app/services/locale/locale.service';
import { TranslateService } from '@ngx-translate/core';
import { debounce } from 'lodash';

@Component({
    selector: 'pixie-homepage',
    templateUrl: './homepage.component.html',
    styleUrls: ['./homepage.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class HomepageComponent implements OnInit {

    id = '';
    title = '';
    ctas = '';
    cruiseListURL = '';
    contextPath = '';
    locale = 'en-us';
    filters: FilterOptionData;
    isDclClRefactorPhase2: boolean;
    isDclAdvancedBooking: boolean;
    isDclAdvancedBookingPhase2: boolean;
    isDclAdvancedBookingPhase3: boolean;
    isDclShipAdaPhase2: boolean;
    isDclInternationalDirectPhase2: boolean;
    siteId: string;
    baseUrl: string;
    utilsConfig;
    hkdlResourceBundle;
    qqConstants = QUICK_QUOTE_CONSTANTS;
    isNewItinerariesBannerAvailable: boolean;
    hideQuickQuote: boolean;

    constructor(
        private quickQuoteService: QuickQuoteService,
        private cruiseListingService: CruiseListingDataService,
        private configService: ConfigService,
        private travelAgentService: TravelAgentService,
        private analyticsService: HomepageAnalyticsService,
        private httpClient: HttpClient,
        private resourceBundleService: ResourceBundleService,
        private localeService: LocaleService,
        private translate: TranslateService,
        private newItinerariesV3BannerService: NewItinerariesV3BannerService
    ) { }

    async ngOnInit() {
        const featureToggles = this.configService.getValue('featureToggles');
        this.siteId = this.configService.getValue('siteId');
        this.isDclClRefactorPhase2 = !!(featureToggles && featureToggles.dclClRefactorPhase2);
        this.isDclAdvancedBooking = !!(featureToggles && featureToggles.dclAdvancedBooking);
        this.isDclAdvancedBookingPhase2 = !!(featureToggles && featureToggles.dclAdvancedBookingPhase2);
        this.isDclAdvancedBookingPhase3 = !!(featureToggles && featureToggles.dclAdvancedBookingPhase3);
        this.isDclShipAdaPhase2 = !!(featureToggles && featureToggles.dclShipAdaPhase2);
        this.isDclInternationalDirectPhase2 = !!(featureToggles && featureToggles.dclInternationalDirectPhase2);
        this.cruiseListURL = this.configService.getValue('cruiseListURL');
        this.quickQuoteService.onApplyFilters.subscribe((data: string[]) => this.onApplyFilters(data));

        if (this.siteId === 'dcl') {
            await this.initDclQuickQuoteConfig();
            this.translate.get('hideQQ')
                .subscribe((translation: string) => {
                    const hideQQ = translation.toLowerCase();
                    if (hideQQ !== 'true') {
                        this.setDCLQQFilterOptions();
                    } else {
                        this.hideQuickQuote = true;
                    }
                }
                );
        }
        if (this.siteId === 'hkdl') {
            this.initHkdlQuickQuoteConfig();
            this.initHkdlQuickQuoteContent();
        }

        this.travelAgentService.setFlowEnabled(true);
        this.travelAgentService.setRestBaseUrl(this.configService.getValue('travelAgentWebApiUrl'));

        this.orderElements();

        window.addEventListener('resize', debounce(() => {
            this.orderElements();
        }, 100));
    }

    /** Places elements in the correct order in the DOM so that keyboard navigation order matches */
    orderElements = () => {
        const homepageTop = document.getElementById('homepage-top');
        const qqContainer = document.getElementById('quick-quote-container');
        const pixieHero = document.getElementsByTagName('pixie-hero-dcl')[0];
        const windowSize = window.innerWidth;
        if (windowSize >= 1024) { // Desktop size
            homepageTop.prepend(qqContainer);
        } else { // Mobile size
            homepageTop.prepend(pixieHero);
        }
    }

    onApplyFilters(filters: string[]): void {
        /*
        if (window.location.hash) {
            console.log('qq hash ', window.location.hash);
            this.cruiseListURL += window.location.hash;
        }
        */
        window.location.href = this.cruiseListURL;
    }

    setDCLQQFilterOptions(): void {
        this.cruiseListingService.getFilters(this.locale).subscribe(
            (filters: FilterOptionData) => {
                this.contextPath = 'wdpr-web-dcl-homepage';
                this.filters = filters;
                // This is needed to wait until quick quote is initialized with the filters
                setTimeout(() => {
                    this.initNewSailingBanner();
                });
            },
            () => console.log('qq error')
        );
    }

    /**
     * Receives the analytics data from the respective triggered Event Emitter
     * @param analyticsData object with the values needed for Analytics Function.
     */
    analyticsTrackingEvent(analyticsData: CruiseListingAnalyticsModel): void {
        console.log('qq analytics event');
        analyticsData.pageVariant = ANALYTICS_CONSTANTS.PAGE_VARIANT;
        analyticsData.bookingSearchPartySize = analyticsData.bookingSearchPartySize;
        analyticsData.bookingSearchNumResults = analyticsData.bookingSearchNumResults;
        analyticsData.facetsUtilized = analyticsData.facetsUtilized;
        analyticsData.productsDisplayedEvent = analyticsData.productsDisplayedEvent;
        analyticsData.productsDisplayed = analyticsData.productsDisplayed;
        this.analyticsService.trackLink(analyticsData.trackLinkID, analyticsData);
    }

    /**
     * Make call to utils and d-scribe to get the configs and contents for HKDL QQ
     */
    public initHkdlQuickQuoteConfig(): void {
        this.httpClient.get(
            `${ENDPOINTS.utilsConfig}`
        ).subscribe((config) => {
            this.utilsConfig = config;
        });
    }

    public initDclQuickQuoteConfig(): void {
        this.localeService.getLocale().then((locale: string) => {
            this.locale = locale;
        });
    }

    /**
     * Get the content from d-scribe call
     */
    public initHkdlQuickQuoteContent(): void {
        this.resourceBundleService.get('quickquote').then((resourceBundle) => {
            this.hkdlResourceBundle = resourceBundle;
        });
    }

    /**
     * Initialize the New Sailing banner filter
     */
    private initNewSailingBanner(): void {
        const translationKey = this.qqConstants.translationKeys.advancedBookingBanner.filters;
        this.translate.get('hideQQ')
            .subscribe((translation: string) => {
                console.log('qq translation ', translation);
            }
            );
        if (this.isDclAdvancedBookingPhase3) {
            this.translate.get(translationKey)
                .subscribe((translation: string) => {
                    const filterName = translation !== translationKey ? translation : undefined;

                    this.newItinerariesV3BannerService.setFilterBannerType(filterName);
                    this.isNewItinerariesBannerAvailable = true;
                }
                );
        }
    }
}
